import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CountBadgePipe } from '../../../../../core/pipes/badge.pipe';
import { AuthService } from '../../../../auth/auth.service';
import { WorkQueueItem, WorkQueueService } from '../../../services/work-queue.service';

@Component({
  selector: 'app-work-queue-list',
  standalone: true,
  imports: [NgClass, DatePipe, CountBadgePipe, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, MatButtonModule],
  templateUrl: './work-queue-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkQueueListComponent implements OnInit, OnChanges {
  private workQueueService = inject(WorkQueueService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() filters: any = {};
  @Input() title = 'Work Queue';
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  items: WorkQueueItem[] = [];
  totalItems = 0;
  loading = false;
  error: string | null = null;

  readonly priorityConfig: Record<string, { label: string; textClass: string; bgClass: string; dotClass: string; borderClass: string }> = {
    critical: { label: 'Critical', textClass: 'text-red-700', bgClass: 'bg-red-100', dotClass: 'bg-red-500', borderClass: 'border-l-red-500' },
    high: { label: 'High', textClass: 'text-orange-700', bgClass: 'bg-orange-100', dotClass: 'bg-orange-500', borderClass: 'border-l-orange-500' },
    medium: { label: 'Medium', textClass: 'text-yellow-700', bgClass: 'bg-yellow-100', dotClass: 'bg-yellow-500', borderClass: 'border-l-yellow-500' },
    low: { label: 'Low', textClass: 'text-blue-700', bgClass: 'bg-blue-100', dotClass: 'bg-blue-400', borderClass: 'border-l-blue-400' },
  };

  ngOnInit(): void {
    if (!this.hasPermission) return;
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.load();
    }
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.workQueueService
      .getMyQueue(this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = res.record ?? {};
          this.items = data.items ?? [];
          this.totalItems = data.total ?? 0;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getPriorityConfig(priority: string) {
    return this.priorityConfig[priority] ?? this.priorityConfig['medium'];
  }

  navigate(item: WorkQueueItem): void {
    switch (item.item_type) {
      case 'service_request':
      case 'owner_request':
        this.router.navigate(['/eservices/list'], { queryParams: { id: item.source_entity_id } });
        break;
      case 'bill':
        this.router.navigate(['/accounting/bills'], { queryParams: { billId: item.source_entity_id } });
        break;
      case 'work_order':
      case 'contract_renewal':
      case 'procurement_request':
        this.router.navigate(['/procurement/work-orders/detail', item.source_entity_id]);
        break;
      case 'task':
        this.router.navigate(['/tasks/detail', item.source_entity_id]);
        break;
      case 'violation_notice':
        this.router.navigate(['/violations/notices'], { queryParams: { id: item.source_entity_id } });
        break;
      case 'online_enquiry':
        this.router.navigate(['/happiness-center/online'], { queryParams: { happiness_center_id: item.source_entity_id } });
        break;
      case 'expiring_compliance':
        this.router.navigate(['/compliances/expiring']);
        break;
      default:
        this.router.navigateByUrl(item.url_path);
    }
  }

  getItemTypeLabel(itemType: string): string {
    const labels: Record<string, string> = {
      service_request: 'Service Request',
      owner_request: 'Owner Request',
      bill: 'Vendor Bill',
      work_order: 'Work Order',
      contract_renewal: 'Contract',
      task: 'Task',
      violation_notice: 'Violation Notice',
      online_enquiry: 'Online Enquiry',
      expiring_compliance: 'Compliance',
    };
    return labels[itemType] ?? itemType;
  }

  getModuleIcon(module: string): string {
    const icons: Record<string, string> = {
      eservices: 'assignment',
      accounting: 'receipt_long',
      procurement: 'construction',
      contracts: 'description',
      audit: 'fact_check',
      compliance: 'verified_user',
      inspections: 'search',
      tasks: 'task_alt',
      violations: 'gavel',
      happiness: 'sentiment_satisfied',
    };
    return icons[module] ?? 'task';
  }
}
